/**
 * What the permission screen governs, beyond the document types.
 *
 * The reference's matrix has three groups of rows — Transactions, Masters and
 * Reports — and a second tab of "Special Actions": single actions on a
 * document (pre-close, reopen, mark as closed, set up a purchase plan…) that a
 * business wants to keep to particular roles on top of the base permission.
 *
 * The keys live in `role_permission.doc_type` beside the document types, with
 * a prefix that cannot collide with one: `master:item`, `report:po_execution`,
 * `dashboard:project_overview`.
 */
import { DOCUMENTS, getDocumentDef } from '../documents/registry.js'
import { MASTER_DEFS, getMasterDef } from '../masters/registry.js'
import { REPORT_DEFS, getReportDef } from '../reports/registry.js'
import { DASHBOARD_DEFS, getDashboardDef } from '../dashboards/registry.js'

export type AccessLevel = 'NONE' | 'VIEW' | 'CREATE' | 'APPROVE' | 'FULL_ACCESS'
export type AccessGroup = 'TRANSACTION' | 'MASTER' | 'REPORT'

export const ACCESS_GROUP_LABELS: Readonly<Record<AccessGroup, string>> = {
  TRANSACTION: 'Transactions',
  MASTER: 'Masters',
  REPORT: 'Reports and dashboards',
}

export const masterAccessKey = (key: string): string => `master:${key}`
export const reportAccessKey = (key: string): string => `report:${key}`
export const dashboardAccessKey = (key: string): string => `dashboard:${key}`

export const accessGroupOf = (key: string): AccessGroup =>
  key.startsWith('master:') ? 'MASTER'
    : key.startsWith('report:') || key.startsWith('dashboard:') ? 'REPORT'
      : 'TRANSACTION'

/** The levels each group can be set to. A report is seen or it is not. */
export const LEVELS_FOR_GROUP: Readonly<Record<AccessGroup, readonly AccessLevel[]>> = {
  TRANSACTION: ['NONE', 'VIEW', 'CREATE', 'APPROVE', 'FULL_ACCESS'],
  MASTER: ['NONE', 'VIEW', 'CREATE', 'FULL_ACCESS'],
  REPORT: ['NONE', 'VIEW'],
}

export interface AccessRowDef {
  readonly key: string
  readonly label: string
  readonly group: AccessGroup
}

/** Every row of the matrix, in the order the screen shows them. */
export const ACCESS_ROWS: readonly AccessRowDef[] = [
  ...DOCUMENTS.map((d) => ({ key: d.key, label: d.labelPlural, group: 'TRANSACTION' as const })),
  ...MASTER_DEFS.map((m) => ({ key: masterAccessKey(m.key), label: m.labelPlural, group: 'MASTER' as const })),
  ...REPORT_DEFS.map((r) => ({ key: reportAccessKey(r.key), label: r.label, group: 'REPORT' as const })),
  ...DASHBOARD_DEFS.map((d) => ({ key: dashboardAccessKey(d.key), label: `${d.label} (dashboard)`, group: 'REPORT' as const })),
]

const ROWS_BY_KEY = new Map(ACCESS_ROWS.map((r) => [r.key, r]))
export const getAccessRow = (key: string): AccessRowDef | undefined => ROWS_BY_KEY.get(key)

/** What a key is called, in a refusal or a changelog line. */
export const accessLabel = (key: string): string => {
  const doc = getDocumentDef(key)
  if (doc) return doc.labelPlural
  if (key.startsWith('master:')) return getMasterDef(key.slice(7))?.labelPlural ?? key
  if (key.startsWith('report:')) return getReportDef(key.slice(7))?.label ?? key
  if (key.startsWith('dashboard:')) return getDashboardDef(key.slice(10))?.label ?? key
  return key
}

/**
 * What one level allows on one row — the ⓘ beside every cell (USR-009).
 *
 * Written per group, and for a document type it names the actions the type
 * actually has, so "Approve" on a type with no approval says so.
 */
export const levelMeaning = (key: string, level: AccessLevel): string => {
  const group = accessGroupOf(key)
  const label = accessLabel(key).toLowerCase()
  if (level === 'NONE') return `Cannot see ${label}; they are left off the menu.`
  if (group === 'REPORT') return `May open ${label} and run it within their project scope.`
  if (group === 'MASTER') {
    switch (level) {
      case 'VIEW': return `May look up and open ${label}, but not add or change them.`
      case 'CREATE': return `May add ${label} and change them.`
      default: return `May add and change ${label}, and retire or restore them.`
    }
  }
  const def = getDocumentDef(key)
  const has = (a: string): boolean => (def?.actions as readonly string[] | undefined)?.includes(a) ?? false
  switch (level) {
    case 'VIEW': return `May open and print ${label}, but not raise or change them.`
    case 'CREATE': return `May raise, save and submit ${label}${has('preClose') ? ', and pre-close them' : ''}.`
    case 'APPROVE': return has('approve')
      ? `Everything Create allows, and may approve or return ${label} at a step they are named on.`
      : `Everything Create allows. ${def?.labelPlural ?? 'These'} have no approval, so this adds nothing.`
    default: return `Everything Approve allows, and may cancel and reopen ${label}${has('undoPreClose') ? ' and undo a pre-close' : ''}.`
  }
}

/* ---- special actions (USR-007) ------------------------------------------ */

export interface SpecialActionDef {
  readonly docType: string
  /** The action key the server checks: a document `ActionKey` or one of its own. */
  readonly action: string
  readonly label: string
  /** The level on the document type somebody needs before roles are even considered. */
  readonly baseLevel: AccessLevel
}

const sa = (docType: string, action: string, label: string, baseLevel: AccessLevel): SpecialActionDef =>
  ({ docType, action, label, baseLevel })

/**
 * The reference's Special Actions tab, row for row, where we have the document.
 *
 * Enquiries and spare requests are modules this system does not have. A row
 * whose action is not on the document yet (Mark as Closed on a project, Suspend
 * on a subcontract order…) is still configurable: the action is checked
 * through `assertSpecialAction` the moment it is built.
 */
export const SPECIAL_ACTIONS: readonly SpecialActionDef[] = [
  sa('project', 'markClosed', 'Mark as Closed', 'APPROVE'),
  sa('project', 'markOpen', 'Mark as Open', 'APPROVE'),
  sa('material_request', 'setPurchasePlan', 'Set up Purchase Plan', 'VIEW'),
  sa('material_request', 'preClose', 'Pre Close', 'CREATE'),
  sa('material_request', 'undoPreClose', 'Reopen', 'FULL_ACCESS'),
  sa('purchase_requisition', 'preClose', 'Pre Close', 'CREATE'),
  sa('purchase_requisition', 'undoPreClose', 'Reopen', 'FULL_ACCESS'),
  sa('request_for_quote', 'markClosed', 'Mark as Closed', 'APPROVE'),
  sa('request_for_quote', 'markOpen', 'Mark as Open', 'APPROVE'),
  sa('supplier_quotation', 'setDecision', 'Set Decision', 'CREATE'),
  sa('purchase_order', 'preClose', 'Pre Close', 'CREATE'),
  sa('purchase_order', 'undoPreClose', 'Reopen', 'FULL_ACCESS'),
  sa('subcontract_order', 'preClose', 'Pre Close', 'CREATE'),
  sa('subcontract_order', 'undoPreClose', 'Reopen', 'FULL_ACCESS'),
  sa('subcontract_order', 'suspend', 'Suspend Pending Work', 'CREATE'),
  sa('subcontract_order', 'resume', 'Resume Suspended', 'CREATE'),
  sa('stock_purchase_request', 'preClose', 'Pre Close', 'CREATE'),
  sa('stock_purchase_request', 'undoPreClose', 'Reopen', 'FULL_ACCESS'),
  sa('tool_request', 'preClose', 'Pre Close', 'CREATE'),
  sa('tool_request', 'undoPreClose', 'Reopen', 'FULL_ACCESS'),
  sa('tool_issue', 'extendReturnDate', 'Extend Return Date', 'CREATE'),
]

export const getSpecialAction = (docType: string, action: string): SpecialActionDef | undefined =>
  SPECIAL_ACTIONS.find((s) => s.docType === docType && s.action === action)

/**
 * Actions the server runs outside the document's own action list — from a
 * work list or a comparative statement — and that are therefore live even
 * though `def.actions` does not name them.
 */
const SERVED_ELSEWHERE: ReadonlySet<string> = new Set([
  'material_request:setPurchasePlan', 'supplier_quotation:setDecision', 'project:markClosed', 'project:markOpen',
])

/** Whether the document can do this today, or the row is waiting for it. */
export const specialActionAvailable = (s: SpecialActionDef): boolean =>
  SERVED_ELSEWHERE.has(`${s.docType}:${s.action}`)
  || ((getDocumentDef(s.docType)?.actions as readonly string[] | undefined)?.includes(s.action) ?? false)

/** The shape the Special Actions tab reads and writes. */
export interface SpecialActionRow extends SpecialActionDef {
  readonly docTypeLabel: string
  readonly available: boolean
  /** ALL: anyone holding the base permission. SPECIFIC: only the roles listed. */
  readonly mode: 'ALL' | 'SPECIFIC'
  /** Sign-in roles (OWNER/ADMIN/MEMBER) or business role codes. */
  readonly roles: readonly string[]
  readonly setAt: string | null
  readonly setBy: string | null
}

/* ---- workflow alerts (WF-011) ------------------------------------------- */

export interface WorkflowAlertAction { readonly action: string; readonly label: string }

const ALERT_ACTIONS: readonly WorkflowAlertAction[] = [
  { action: 'submit', label: 'Submit' },
  { action: 'approve', label: 'Approve' },
  { action: 'reject', label: 'Return for Changes' },
  { action: 'cancel', label: 'Cancel' },
  { action: 'preClose', label: 'Pre Close' },
  { action: 'undoPreClose', label: 'Reopen' },
  { action: 'setPurchasePlan', label: 'Set up Purchase Plan' },
]

/** The actions a route's Alerts table lists for one document type. */
export const alertActionsFor = (docType: string): readonly WorkflowAlertAction[] => {
  const actions = (getDocumentDef(docType)?.actions as readonly string[] | undefined) ?? []
  return ALERT_ACTIONS.filter((a) => a.action === 'setPurchasePlan'
    ? docType === 'material_request'
    : actions.includes(a.action) || a.action === 'submit' || (a.action === 'approve' && actions.includes('approve')))
}

/** One row of a route's Alerts table: who is told when this happens. */
export interface WorkflowAlert {
  readonly action: string
  readonly projectRoleIds: readonly string[]
  readonly roleIds: readonly string[]
  readonly userIds: readonly string[]
}

/* ---- administration lists (SET-030 on admin screens) --------------------- */

/**
 * What a saved view on an administration list may hold: the list's filter
 * keys and column keys. The server's view allow-list reads this, so a view
 * can only say what the list can show.
 */
export const ADMIN_LISTS: Readonly<Record<string, { readonly label: string; readonly filters: readonly string[]; readonly columns: readonly string[] }>> = {
  users: {
    label: 'Users',
    filters: ['name', 'type', 'role', 'active'],
    columns: ['name', 'email', 'roleName', 'userType', 'projectScope', 'active', 'designation', 'lastLoginAt', 'communicationEmail', 'createdAt'],
  },
  numbering: {
    label: 'Document Number Rules',
    filters: ['docType', 'active'],
    columns: ['docTypeLabel', 'active', 'format', 'separately', 'resetPeriod', 'width', 'nextValue', 'example'],
  },
  workflows: {
    label: 'Workflows',
    filters: ['docType', 'company', 'wfNo', 'status'],
    columns: ['wfNo', 'docTypeLabel', 'companies', 'status', 'overrideUsers', 'createdAt', 'createdByName', 'description'],
  },
  companies: {
    label: 'Companies',
    filters: ['name', 'training', 'active'],
    columns: ['name', 'legalName', 'abbreviation', 'isTraining', 'active', 'gstin', 'panNo'],
  },
}
